import axios from "axios";
import { HOST_API } from "../config";

const axiosInstance = axios.create({
  baseURL: HOST_API,
  headers: {
    "Content-Type": "multipart/form-data",
  },
});

const REFRESH_PATH = "api/refresh";
const LOGIN_PATH = "api/login";

const clearAuthAndRedirect = () => {
  console.log('[Axios] Clearing all auth data and redirecting to login');
  localStorage.removeItem("accessToken");
  localStorage.removeItem("isAdmin");
  localStorage.removeItem("isSubAdmin");
  localStorage.removeItem("isImpersonate");
  localStorage.removeItem("source_id");
  localStorage.removeItem("menu");
  localStorage.removeItem("rememberMe");
  window.location = "/auth/login";
};

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;
    const originalRequest = error?.config || {};
    const requestUrl = originalRequest?.url || "";

    if (status === 401) {
      // Never try to refresh while calling /api/refresh itself or /api/login
      const isRefreshCall = requestUrl.includes(REFRESH_PATH);
      const isLoginCall = requestUrl.includes(LOGIN_PATH);
      // Absolute URLs that don't target our backend (e.g. the support-agent
      // service) still get the refresh+retry below, but a PERSISTENT 401 from
      // them must NOT log the whole panel out: the fault is theirs, not the
      // session's. Same defense the app added in PR #557.
      const isExternalHost =
        /^https?:\/\//i.test(requestUrl) && !(HOST_API && requestUrl.startsWith(HOST_API));
      const alreadyRetried = originalRequest._retry;
      const rememberMe = (() => {
        try { return localStorage.getItem("rememberMe") === "1"; } catch { return false; }
      })();

      if (!isRefreshCall && !isLoginCall && !alreadyRetried && rememberMe) {
        try {
          // Lazy require to avoid circular dep at module-init time
          const { refreshAccessToken } = require("./jwt");
          const newToken = await refreshAccessToken();
          if (newToken) {
            originalRequest._retry = true;
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return axiosInstance(originalRequest);
          }
        } catch (refreshErr) {
          console.warn('[Axios] Refresh attempt threw', refreshErr);
        }
      }

      if (isExternalHost) return Promise.reject(error);

      // Refresh impossible, not allowed, or already retried → logout
      clearAuthAndRedirect();
      return Promise.reject(error);
    }

    return Promise.reject(
      (error.response && error.response.data) || "Something went wrong"
    );
  }
);

axiosInstance.interceptors.request.use(function (config) {
  const token = localStorage.getItem("accessToken");
  if (token) {
    if (!config.headers["Content-Type"]) {
      config.headers["Content-Type"] = "application/x-www-form-urlencoded";
    }
    config.headers.Authorization = "Bearer " + token;
    // Telemetry header for OUR backend only. On external hosts (absolute
    // URLs, e.g. the support-agent service) a custom header fails their CORS
    // preflight unless allowlisted — and it's none of their business anyway.
    const url = config.url || "";
    const isExternalHost =
      /^https?:\/\//i.test(url) && !(HOST_API && url.startsWith(HOST_API));
    if (!isExternalHost) {
      config.headers["front-end-path"] = window.location.pathname;
    }
  }
  return config;
});

export default axiosInstance;
